import React, { useEffect, useId, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import { cn } from './cn'

/**
 * `app`     → web 側欄連結（預設 <a>；web 注入 Next <Link>）。
 * `icon`    → App 面側欄（design-guideline §3.5）：圖示＋單行名稱、滑鼠列高、
 *             目前頁是墨色字重加左側 2px 墨條；尾端可放灰色數字，或在「這一頁有事要處理」
 *             時換成赭石警示，滑過或鍵盤聚焦浮出說明卡。預設 <button>。
 *
 * 兩種共用的是「結構與行為」（active→aria-current），長相各自由 variant 決定。
 */
export type SideNavVariant = 'app' | 'icon'

/** The trailing text's colour on the `icon` variant: grey, or the red of "recording now". */
export type SideNavEndTone = 'muted' | 'live'

export interface SideNavWarning {
  /** 赭石警示的可及名稱，併進項目名稱（「設定 快捷鍵還不能用」）。 */
  label: string
  /** 說明卡第一行（赭石、帶圖示）。 */
  title: React.ReactNode
  /** 缺什麼。 */
  description: React.ReactNode
  /** 點下去會發生什麼（純文字；整格本身就是入口）。 */
  action?: React.ReactNode
}

interface SideNavItemCommonProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  /** Visible label (i18n at the call site). */
  label: React.ReactNode
  /** Marks the current page → `aria-current="page"` + active styling. */
  active?: boolean
  /** Element to render as. Default: app→'a', icon→'button'. Web passes Link. */
  as?: React.ElementType
  /** Anchor target (app variant). Passed through to the rendered element. */
  href?: string
  /** Anchor target attribute (e.g. '_blank' for an external link). Passed through. */
  target?: string
  /** Anchor rel attribute (e.g. 'noreferrer'). Passed through. */
  rel?: string
}

interface SideNavTextItemProps extends SideNavItemCommonProps {
  variant: 'app'
  /** Optional sub-line under the label. */
  hint?: React.ReactNode
}

interface SideNavIconItemProps extends SideNavItemCommonProps {
  variant: 'icon'
  icon: React.ReactNode
  /** Muted trailing text, e.g. a count. Replaced by the mark while `warning` is set. */
  end?: React.ReactNode
  /** `live` paints `end` in the brand red (a recording clock); text, so not a solid red. */
  endTone?: SideNavEndTone
  /** Set only while something here needs attention; nothing shows when it is fine. */
  warning?: SideNavWarning
}

export type SideNavItemProps = SideNavTextItemProps | SideNavIconItemProps

export function SideNavItem(props: SideNavItemProps) {
  if (props.variant === 'icon') return <IconSideNavItem {...props} />
  return <TextSideNavItem {...props} />
}

function TextSideNavItem({
  variant,
  label,
  hint,
  active = false,
  as,
  href,
  className,
  ...rest
}: SideNavTextItemProps) {
  const ariaCurrent = active ? 'page' : undefined

  const Tag = as ?? 'a'
  return (
    <Tag
      data-side-nav-item={variant}
      className={cn(
        'block whitespace-nowrap rounded-[var(--radius)] border border-transparent px-2.5 py-2 text-base text-foreground transition-colors hover:bg-[var(--fg-soft)] focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)] aria-[current=page]:border-border aria-[current=page]:bg-background aria-[current=page]:font-medium',
        className
      )}
      aria-current={ariaCurrent}
      href={href}
      {...rest}
    >
      {label}
      {hint != null ? <span className="block text-xs text-muted">{hint}</span> : null}
    </Tag>
  )
}

function IconSideNavItem({
  variant,
  icon,
  label,
  end,
  endTone = 'muted',
  warning,
  active = false,
  as,
  href,
  className,
  ...rest
}: SideNavIconItemProps) {
  const Tag = as ?? 'button'
  const tagProps = Tag === 'button' ? { type: 'button' as const } : {}
  const cardId = useId()

  const item = (
    <Tag
      {...tagProps}
      data-side-nav-item={variant}
      className={cn(
        // The current-page bar sits in the sidebar's 10px inset, at its left edge.
        'relative flex h-[var(--d-row)] w-full items-center gap-2.5 rounded-[var(--radius)] border-0 bg-transparent px-2.5 text-left text-md text-muted transition-colors hover:bg-[var(--fg-soft)] hover:text-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--focus-ring)] aria-[current=page]:font-semibold aria-[current=page]:text-foreground aria-[current=page]:before:absolute aria-[current=page]:before:inset-y-2 aria-[current=page]:before:-left-2.5 aria-[current=page]:before:w-0.5 aria-[current=page]:before:bg-foreground',
        className
      )}
      aria-current={active ? 'page' : undefined}
      aria-describedby={warning ? cardId : undefined}
      href={href}
      {...rest}
    >
      <span
        className="flex size-5 shrink-0 items-center justify-center [&>svg]:size-5"
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="min-w-0 truncate">{label}</span>
      {warning ? (
        <span className="ml-auto inline-flex shrink-0 text-warn">
          <TriangleAlert aria-hidden="true" className="size-4" />
          <span className="sr-only">{warning.label}</span>
        </span>
      ) : end != null ? (
        <span
          className={cn(
            'ml-auto shrink-0 text-xs font-normal tabular-nums',
            endTone === 'live' ? 'text-brand' : 'text-muted'
          )}
        >
          {end}
        </span>
      ) : null}
    </Tag>
  )

  if (!warning) return item
  return (
    <WarningCard id={cardId} warning={warning}>
      {item}
    </WarningCard>
  )
}

/**
 * The hover card beside a warned item. It is the item's SIBLING, not its child,
 * so its words never join the button's accessible name while it shows; the
 * button points at it with `aria-describedby` instead. Escape puts it away
 * (WCAG 1.4.13) until the pointer leaves or focus moves on.
 */
function WarningCard({
  id,
  warning,
  children,
}: {
  id: string
  warning: SideNavWarning
  children: React.ReactNode
}) {
  const [hovered, setHovered] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Hovered without focus, Escape lands on <body>, not on the item.
  useEffect(() => {
    if (!hovered) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDismissed(true)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [hovered])

  return (
    <div
      className="group relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false)
        setDismissed(false)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setDismissed(true)
      }}
      onBlur={() => setDismissed(false)}
    >
      {children}
      <span
        id={id}
        role="tooltip"
        className={cn(
          'pointer-events-none absolute left-[calc(100%+--spacing(3))] top-1/2 z-[70] w-67 -translate-y-1/2 whitespace-normal rounded-[var(--radius-lg)] bg-background px-3.5 py-3 text-left font-normal text-foreground shadow-[var(--shadow-pop)]',
          dismissed ? 'hidden' : 'hidden group-hover:block group-has-[:focus-visible]:block'
        )}
      >
        <span className="flex items-center gap-1.5 text-sm font-semibold text-warn">
          <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
          {warning.title}
        </span>
        <span className="mt-1 block text-xs leading-[1.6] text-muted">{warning.description}</span>
        {warning.action != null ? (
          <span className="mt-2 block text-xs text-foreground">{warning.action}</span>
        ) : null}
      </span>
    </div>
  )
}
